import { createSelector } from 'reselect';

import type { RootState } from 'config/store/reducers';
import { PREVIEW, PROVIEW } from 'constants/Parameters';
import { getLastMessageByDate, groupMessagesByDate } from 'common/Messages/utils';

import { getEntity } from 'modules/entity/selectors';
import { isStock } from 'modules/entity/subEntities/intervention/selectors';

const getState = (state: RootState) => state.chat;
const getRoot = (state: RootState) => state;
const getProps = (_: RootState, prop: string[]): string[] => prop;

const getData = createSelector(getState, (state) => state.data);
const getUI = createSelector(getState, (state) => state.ui);

const getSendState = createSelector(getUI, (ui) => ui.send);
const getDisplayedMessagesIds = createSelector(getUI, (ui) => ui.displayedMessagesIds);
export const getReceivedAt = createSelector(getUI, (ui) => ui.receivedMessageAt);

const getAllMessages = createSelector(getData, (data) => data.byId);

const getAllMessagesData = createSelector(getAllMessages, (messages) => Object.values(messages));
const getMessagesByIds = createSelector(
  getAllMessages,
  getProps,
  (messages, ids) => ids.map((id) => messages[id])
);

const getMessages = createSelector(getRoot, getDisplayedMessagesIds, getMessagesByIds);
const getReversedMessages = createSelector(getMessages, (messages) => [...messages].reverse());

export const getFirstUnreadMessageId = createSelector(
  getReversedMessages,
  (messages) => messages.find(({ unread }) => unread)?.id
);

export const getGroupedMessagesByDate = createSelector(getReversedMessages, groupMessagesByDate);

export const getLatestMessage = createSelector(getMessages, (messages) => messages?.[0]);

export const isLoadingSend = createSelector(getSendState, (send) => send.isLoading);

const isReadingMessages = createSelector(getUI, (ui) => ui.isReadingMessages);

const getUnreadMessages = createSelector(
  getAllMessagesData,
  (messages) => messages.filter(({ unread }) => Boolean(unread))
);

export const getLastUnreadMessage = createSelector(getUnreadMessages, getLastMessageByDate);

export const countUnreadMessages = createSelector(getUnreadMessages, (messages) => messages.length);
const hasUnreadMessages = createSelector(countUnreadMessages, (nbUnread) => nbUnread > 0);

export const isLoadingMessages = createSelector(getUI, (ui) => ui.isLoadingMessages);

export const getNextMessagesUrl = createSelector(getUI, (ui) => ui.getNextMessagesUrl);

export const isLoadingNextMessages = createSelector(getUI, (ui) => ui.isLoadingNextMessages);

export const getReadMessagesUrl = createSelector(getEntity, (entity) => entity.readMessagesUrl);

export const getAddMessageUrl = createSelector(getEntity, (entity) => entity.addMessageUrl);

const hasMessages = createSelector(getMessages, (messages = []) => messages.length > 0);

export const canDisplayMessages = createSelector(
  getEntity,
  hasMessages,
  isStock,
  ({ addMessageUrl }, hasMsg, isStockVO) => (hasMsg || addMessageUrl) && !isStockVO && !PROVIEW
);

export const canRead = createSelector(
  isReadingMessages,
  hasUnreadMessages,
  getReadMessagesUrl,
  (isReading, hasUnread, readUrl) => !PREVIEW && !isReading && hasUnread && Boolean(readUrl)
);

export const isSendDisabled = createSelector(
  getEntity,
  ({ addMessageUrl }) => PREVIEW || !addMessageUrl
);

export const getAllChatMedias = createSelector(
  getAllMessagesData,
  (messages) => messages.flatMap(({ medias = [] }) => medias)
);
